import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  FileCheck,
  Loader2,
  Package,
  Calendar,
  Warehouse,
  Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSessionState } from '@/hooks/use-session-state';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';

export function GoodsReceiptsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poIdFromUrl = searchParams.get('poId');

  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:receipts:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedWarehouse, setSelectedWarehouse] = useSessionState<string>(
    'trading:receipts:selectedWarehouse',
    'all'
  );
  const [page, setPage] = useState(1);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data: warehousesData } = trpc.warehouse.list.useQuery({});

  const { data, isLoading, error } = trpc.tradingGoodsReceipt.list.useQuery({
    search: debouncedSearch || undefined,
    warehouseId: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
    purchaseOrderId: poIdFromUrl || undefined,
    page,
  });

  const warehouses = warehousesData ?? [];
  const receipts = data?.receipts ?? [];
  const pagination = data?.pagination;

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('purchasing.goodsReceipts.title')}</h1>
          <p className="text-muted-foreground">{t('purchasing.goodsReceipts.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/purchasing/receipts/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('purchasing.goodsReceipts.addReceipt')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('purchasing.goodsReceipts.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('purchasing.goodsReceipts.warehouse')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.stockLevels.allWarehouses')}</SelectItem>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && receipts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileCheck className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('purchasing.goodsReceipts.noReceipts')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('purchasing.goodsReceipts.noReceiptsDesc')}
          </p>
        </div>
      )}

      {/* Goods Receipts Table */}
      {!isLoading && !error && receipts.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('purchasing.goodsReceipts.receiptNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('purchasing.goodsReceipts.purchaseOrder')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('purchasing.goodsReceipts.supplier')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('purchasing.goodsReceipts.warehouse')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('purchasing.goodsReceipts.receiptDate')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('purchasing.goodsReceipts.items')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('purchasing.goodsReceipts.totalValue')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('purchasing.goodsReceipts.landedCostsLabel')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Link
                        to={`/trading/purchasing/receipts/${receipt.id}`}
                        className="text-primary font-mono text-sm font-medium hover:underline"
                      >
                        {receipt.receiptNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/trading/purchasing/${receipt.purchaseOrderId}`}
                        className="text-muted-foreground hover:text-primary font-mono text-sm"
                      >
                        {receipt.purchaseOrderNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">{receipt.supplierName}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Warehouse className="text-muted-foreground h-4 w-4" />
                        {receipt.warehouseName}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="text-muted-foreground h-4 w-4" />
                        {formatDate(receipt.receiptDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 text-sm">
                        <Package className="text-muted-foreground h-4 w-4" />
                        {receipt.itemCount}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-medium">
                        {/* True TRY total (subtotal x frozen fxRate + landed TRY);
                            legacy receipts without a frozen rate fall back to the
                            PO-currency subtotal, labeled as such. */}
                        {receipt.totalValueTry != null
                          ? formatCurrency(receipt.totalValueTry)
                          : receipt.itemsSubtotal != null
                            ? formatCurrency(receipt.itemsSubtotal, receipt.currency)
                            : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {receipt.hasLandedCosts ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          <Truck className="h-3 w-3" />
                          {t('purchasing.goodsReceipts.included')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border bg-background hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border bg-background hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
